import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Building2, CalendarDays, CheckCircle2, ExternalLink, Mail, Phone, Target, UserCheck2, XCircle } from 'lucide-react';

const statusColors: Record<string, string> = {
  nuevo: 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20',
  contactado: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  calificado: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  descartado: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
  convertido: 'bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20',
};

const sourceLabels: Record<string, string> = {
  web: 'Web',
  referido: 'Referido',
  evento: 'Evento',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const emptyQualification = { budget: 'No', authority: 'No', need: 'No', urgency: 'No' };

function formatCLP(value: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value || 0);
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [qualificationOpen, setQualificationOpen] = useState(false);
  const [qualificationForm, setQualificationForm] = useState(emptyQualification);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({
    opportunity_name: '',
    monto_neto: 0,
    stage: 'Prospecto',
    probability: 15,
    next_step: 'Agendar reunion / demo',
    next_step_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  });

  const loadData = useCallback(async () => {
    if (!id) return;
    const [leadRes, companiesRes, contactsRes, oppRes, actRes] = await Promise.all([
      api.get(`/leads/${id}`),
      api.get('/companies'),
      api.get('/contacts'),
      api.get('/opportunities'),
      api.get('/activities'),
    ]);
    setLead(leadRes.data);
    setCompanies(companiesRes.data || []);
    setContacts(contactsRes.data || []);
    setOpportunities((oppRes.data || []).filter((item: any) => item.source_lead_id === id || item.id === leadRes.data?.converted_opportunity_id || item.account_name === leadRes.data?.company));
    setActivities((actRes.data || []).filter((item: any) => item.related_entity_id === id || item.opportunity_id === leadRes.data?.converted_opportunity_id || item.related_to === leadRes.data?.company));
    setQualificationForm({
      budget: leadRes.data?.qualification?.budget || 'No',
      authority: leadRes.data?.qualification?.authority || 'No',
      need: leadRes.data?.qualification?.need || 'No',
      urgency: leadRes.data?.qualification?.urgency || 'No',
    });
    setDiscardReason(leadRes.data?.discard_reason || '');
    setConvertForm({
      opportunity_name: `Oportunidad ${leadRes.data?.company || leadRes.data?.name || ''}`,
      monto_neto: 0,
      stage: 'Prospecto',
      probability: 15,
      next_step: 'Agendar reunion / demo',
      next_step_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    });
  }, [id]);

  useEffect(() => {
    loadData().catch(() => setLead(null));
  }, [loadData]);

  const qualificationSummary = useMemo(() => {
    const score = Number(lead?.qualification?.score || 0);
    return {
      score,
      temperature: lead?.qualification?.temperature || (score >= 75 ? 'Caliente' : score >= 40 ? 'Medio' : 'Frio'),
      qualificationType: lead?.qualification_type || lead?.qualification?.qualification_type || 'Lead',
    };
  }, [lead]);

  const company = companies.find((item) => item.id === lead?.converted_company_id) || companies.find((item) => item.name === lead?.company);
  const convertedContact = contacts.find((item) => item.id === lead?.converted_contact_id);

  if (!id) return null;
  if (!lead) return <div className="p-6 text-sm text-slate-500">Lead no encontrado.</div>;

  const handleQualify = async () => {
    try {
      await api.post(`/leads/${lead.id}/qualify`, qualificationForm);
      toast.success('Lead calificado');
      setQualificationOpen(false);
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible calificar');
    }
  };

  const handleDiscard = async () => {
    try {
      await api.post(`/leads/${lead.id}/discard`, { discard_reason: discardReason });
      toast.success('Lead descartado');
      setDiscardOpen(false);
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible descartar');
    }
  };

  const handleConvert = async () => {
    try {
      await api.post(`/leads/${lead.id}/convert`, convertForm);
      toast.success('Lead convertido');
      setConvertOpen(false);
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible convertir');
    }
  };

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="h-1.5 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
        <div className="flex flex-col gap-4 p-5 md:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-3 min-h-11 gap-2 text-[#475569] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">
              <ArrowLeft size={16} /> Volver
            </Button>
            <h1 className="text-2xl font-bold text-[#0F172A] break-words md:text-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{lead.name}</h1>
            <p className="mt-1 text-sm text-[#475569] break-words">{lead.company || 'Sin empresa asociada'} · {sourceLabels[lead.source] || lead.source}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className={`max-w-full truncate px-3 py-1 text-xs font-semibold capitalize ${statusColors[lead.status] || ''}`}>{lead.status}</Badge>
              <Badge className="max-w-full truncate bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]">{qualificationSummary.qualificationType}</Badge>
              <Badge className="max-w-full truncate bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20 tabular-nums">{qualificationSummary.score} pts</Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {lead.status !== 'convertido' && lead.status !== 'descartado' && (
              <Button variant="outline" onClick={() => setQualificationOpen(true)} className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">
                <UserCheck2 size={16} className="mr-2" /> Calificar
              </Button>
            )}
            {lead.status !== 'convertido' && lead.status !== 'descartado' && (
              <Button variant="outline" onClick={() => setDiscardOpen(true)} className="min-h-11 border-[#FECACA] text-[#B91C1C] focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 sm:min-h-0">
                <XCircle size={16} className="mr-2" /> Descartar
              </Button>
            )}
            {lead.status === 'calificado' && (
              <Button onClick={() => setConvertOpen(true)} className="min-h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">
                <CheckCircle2 size={16} className="mr-2" /> Convertir
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
          <CardHeader><CardTitle>Resumen comercial</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm text-[#475569]">
            <p>
              Este lead entra como <strong>{lead.status}</strong> y debe pasar por contacto, calificación y conversión antes de abrir una oportunidad formal.
              {lead.status === 'convertido' && ` Fue convertido el ${lead.converted_at?.slice(0, 10)}.`}
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">BANT score</p>
                <p className="mt-1 text-lg font-semibold text-[#0F172A]">{qualificationSummary.score}</p>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Temperatura</p>
                <p className="mt-1 text-lg font-semibold text-[#0F172A]">{qualificationSummary.temperature}</p>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Resultado</p>
                <p className="mt-1 text-lg font-semibold text-[#0F172A]">{qualificationSummary.qualificationType}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#8B5CF6] via-[#F59E0B] to-[#2563EB]" />
          <CardHeader><CardTitle>Datos base</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <div className="flex items-center gap-2"><Mail size={15} className="text-[#2563EB]" /> {lead.email || '-'}</div>
            <div className="flex items-center gap-2"><Phone size={15} className="text-[#8B5CF6]" /> {lead.phone || '-'}</div>
            <div className="flex items-center gap-2"><Building2 size={15} className="text-[#22C55E]" /> {lead.company || '-'}</div>
            <div className="flex items-center gap-2"><Target size={15} className="text-[#F59E0B]" /> {sourceLabels[lead.source] || lead.source || '-'}</div>
            <div className="flex items-center gap-2"><CalendarDays size={15} className="text-[#94A3B8]" /> {lead.created_at?.slice(0, 10) || '-'}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#0EA5E9] via-[#2563EB] to-[#8B5CF6]" />
          <CardHeader><CardTitle>Calificacion BANT</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {[
              ['budget', 'Presupuesto'],
              ['authority', 'Autoridad'],
              ['need', 'Necesidad'],
              ['urgency', 'Urgencia'],
            ].map(([key, label]) => (
              <div key={key} className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">{label}</p>
                <p className="mt-2 text-lg font-semibold text-[#0F172A]">{lead.qualification?.[key] || 'No'}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#22C55E] via-[#2563EB] to-[#8B5CF6]" />
          <CardHeader><CardTitle>Conversion</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <p>Empresa: {company ? <Link to={`/empresas/${company.id}`} className="text-[#2563EB] hover:underline">{company.name}</Link> : '-'}</p>
            <p>Contacto: {convertedContact?.name || '-'}</p>
            <p>Oportunidad: {lead.converted_opportunity_id ? <Link to={`/oportunidades/${lead.converted_opportunity_id}`} className="text-[#2563EB] hover:underline">{opportunities.find((item) => item.id === lead.converted_opportunity_id)?.name || lead.converted_opportunity_id}</Link> : '-'}</p>
            {lead.status === 'descartado' && (
              <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3 text-[#B91C1C]">
                Motivo de descarte: {lead.discard_reason || 'No informado'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#F59E0B] via-[#8B5CF6] to-[#2563EB]" />
          <CardHeader><CardTitle>Oportunidades relacionadas</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {opportunities.map((opportunity) => (
              <Link key={opportunity.id} to={`/oportunidades/${opportunity.id}`} className="block rounded-lg border border-[#E2E8F0] p-3 min-h-11 transition-colors hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-[#0F172A] break-words">{opportunity.name}</p>
                    <p className="text-xs text-[#64748B] tabular-nums">{opportunity.stage} · {formatCLP(opportunity.amount || 0)}</p>
                  </div>
                  <ExternalLink size={14} className="shrink-0 text-[#94A3B8]" />
                </div>
              </Link>
            ))}
            {opportunities.length === 0 && <p className="text-sm text-[#94A3B8]">Sin oportunidades relacionadas.</p>}
          </CardContent>
        </Card>

        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-gradient-to-r from-[#22C55E] via-[#2563EB] to-[#8B5CF6]" />
          <CardHeader><CardTitle>Actividades</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="rounded-lg border border-[#E2E8F0] p-3">
                <p className="font-medium text-[#0F172A]">{activity.subject}</p>
                <p className="text-xs text-[#64748B]">{activity.type} · {activity.status}</p>
                <p className="text-xs text-[#64748B]">{activity.due_date || activity.created_at?.slice(0, 10) || '-'}</p>
              </div>
            ))}
            {activities.length === 0 && <p className="text-sm text-[#94A3B8]">Sin actividades relacionadas.</p>}
          </CardContent>
        </Card>
      </div>

      <Dialog open={qualificationOpen} onOpenChange={setQualificationOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle>Calificacion BANT</DialogTitle></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            {[
              ['budget', 'Presupuesto'],
              ['authority', 'Autoridad'],
              ['need', 'Necesidad'],
              ['urgency', 'Urgencia'],
            ].map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">{label}</Label>
                <Select value={qualificationForm[key as keyof typeof qualificationForm]} onValueChange={(value) => setQualificationForm((prev) => ({ ...prev, [key]: value }))}>
                  <SelectTrigger className="border-[#E2E8F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Si">Si</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQualificationOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleQualify} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Descartar lead</DialogTitle></DialogHeader>
          <Textarea value={discardReason} onChange={(e) => setDiscardReason(e.target.value)} className="min-h-[100px] border-[#E2E8F0]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleDiscard} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">Descartar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle>Convertir a oportunidad</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre de oportunidad</Label>
              <Input value={convertForm.opportunity_name} onChange={(e) => setConvertForm((prev) => ({ ...prev, opportunity_name: e.target.value }))} className="border-[#E2E8F0]" />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Monto neto</Label>
                <Input type="number" value={convertForm.monto_neto || ''} onChange={(e) => setConvertForm((prev) => ({ ...prev, monto_neto: Number(e.target.value) || 0 }))} className="border-[#E2E8F0]" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Probabilidad</Label>
                <Input type="number" min={0} max={100} value={convertForm.probability} onChange={(e) => setConvertForm((prev) => ({ ...prev, probability: Number(e.target.value) || 0 }))} className="border-[#E2E8F0]" />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Etapa</Label>
                <Select value={convertForm.stage} onValueChange={(value) => setConvertForm((prev) => ({ ...prev, stage: value }))}>
                  <SelectTrigger className="border-[#E2E8F0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Prospecto', 'Contacto realizado', 'Reunion / demo', 'Propuesta enviada', 'Negociacion'].map((stage) => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Fecha próximo paso</Label>
                <Input type="date" value={convertForm.next_step_date} onChange={(e) => setConvertForm((prev) => ({ ...prev, next_step_date: e.target.value }))} className="border-[#E2E8F0]" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Próximo paso</Label>
              <Textarea value={convertForm.next_step} onChange={(e) => setConvertForm((prev) => ({ ...prev, next_step: e.target.value }))} className="min-h-[90px] border-[#E2E8F0]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleConvert} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Convertir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
